/**
 * AlertDataReceiver.js
 * 处理告警数据接收和解析的专用模块
 */
import Vue from 'vue';
import appConfig from '@/config/app-config';
import signalRManager from './signalr-manager';
import alertFilter from './AlertFilter';

// 事件总线，用于发布告警数据更新事件
const eventBus = new Vue();

class AlertDataReceiver {
  constructor() {
    this._alerts = [];
    this._isConnected = false;
    this._isSubscribed = false;
    this._isInitialized = false; // 添加初始化标志
    this._signalR = null;
    this._options = appConfig.signalR.options;
    this._serverUrl = appConfig.signalR.serverUrl;
    this._topic = appConfig.alerts.topic; // 使用app-config中的配置
    this._messageType = appConfig.alerts.messageType; // 使用app-config中的配置
    
    // 连接和订阅重试
    this._connectingPromise = null;
    this._subscribingPromise = null;
    this._connectRetryCount = 0;
    this._subscribeRetryCount = 0;
    this._maxConnectRetries = 5;
    this._maxSubscribeRetries = 3;
    this._connectRetryInterval = 2000; // 2秒
    this._subscribeRetryInterval = 1500; // 1.5秒
    
    // 限制告警历史记录数量
    this._maxAlertHistory = 100;
    
    // TTS播报配置
    this._ttsEnabled = true;
    this._speechSynthesis = window.speechSynthesis;
    this._speechQueue = [];
    this._isSpeaking = false;
    this._speechWatchdogTimer = null;
    this._speechQueueMax = 20;
    this._speechBlockedWarned = false;
    
    // TTS播报去重配置
    this._speechHistory = new Map(); // 存储播报历史：key=播报内容, value=时间戳
    this._speechDedupeWindow = 5 * 60 * 1000; // 去重时间窗口：5分钟
    this._speechCleanupInterval = 60 * 1000; // 清理间隔：1分钟
    this._startSpeechHistoryCleanup();

    this._onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && this._speechSynthesis && this._speechSynthesis.paused) {
        try {
          this._speechSynthesis.resume();
          console.log('AlertDataReceiver: 页面恢复可见，已恢复语音合成');
        } catch (e) {
          console.warn('AlertDataReceiver: 恢复语音合成失败', e);
        }
      }
    };
    if (typeof document !== 'undefined' && document.addEventListener) {
      document.addEventListener('visibilitychange', this._onVisibilityChange);
    }
  }

  /**
   * 初始化告警数据接收器
   * @param {Object} vueInstance - Vue实例
   * @param {Object} options - 配置选项
   */
  init(vueInstance, options = {}) {
    // 防止重复初始化
    if (this._isInitialized) {
      console.log('AlertDataReceiver已经初始化，跳过重复初始化');
      return;
    }
    
    // 合并选项
    this._options = {
      ...this._options,
      ...options
    };
    
    // 使用signalRManager而非直接创建SignalRClient实例
    this._signalR = signalRManager;
    
    // 确保signalRManager已初始化
    if (!this._signalR._isInitialized) {
      this._signalR.init(this._options);
    }
    
    // 从Vue实例中获取桥梁ID
    if (vueInstance && vueInstance.currentBridgeId !== undefined && vueInstance.currentBridgeId !== null) {
      this._bridgeId = vueInstance.currentBridgeId;
      console.log('AlertDataReceiver从Vue实例获取桥梁ID:', this._bridgeId, '类型:', typeof this._bridgeId);
    } else if (vueInstance && typeof vueInstance.initCurrentBridgeId === 'function') {
      // 尝试初始化桥梁ID
      vueInstance.initCurrentBridgeId();
      this._bridgeId = vueInstance.currentBridgeId;
      console.log('AlertDataReceiver初始化并获取桥梁ID:', this._bridgeId, '类型:', typeof this._bridgeId);
    }
    
    // 标记为已初始化
    this._isInitialized = true;
    
    console.log('AlertDataReceiver已初始化，服务器地址:', this._serverUrl);
  }

  /**
   * 连接到服务器并订阅告警数据
   * @returns {Promise<void>}
   */
  async connect() {
    if (!this._signalR) {
      throw new Error('请先调用init方法初始化AlertDataReceiver');
    }

    // 注册告警消息处理器
    console.log('AlertDataReceiver注册告警消息处理器:', this._messageType);
    this._signalR.onMessage(this._messageType, this._handleAlertMessage.bind(this));
    
    // 注册连接状态变更处理
    this._signalR.onConnectionStatusChanged(this._handleConnectionStatusChanged.bind(this));
    
    // 如果已有连接中的Promise，则返回该Promise
    if (this._connectingPromise) {
      console.log('告警连接已在进行中，等待连接完成...');
      return this._connectingPromise;
    }
    
    // 创建连接Promise
    this._connectingPromise = (async () => {
      try {
        console.log(`AlertDataReceiver正在连接到服务器: ${this._serverUrl}`);
        
        // 确保连接
        if (!this._signalR.isConnected) {
          await this._signalR.connect(this._serverUrl, {
            clientType: 'ALERT_RECEIVER',
            clientId: 'alert_' + Date.now()
          });
          console.log('告警数据接收器连接成功');
        } else {
          console.log('SignalR已连接，复用现有连接');
        }
        
        // 连接成功后，尝试订阅
        await this._subscribeWithRetry();
        
        this._isConnected = true;
        console.log('AlertDataReceiver已连接并订阅告警数据主题');
        
        // 重置重试计数器
        this._connectRetryCount = 0;
        this._subscribeRetryCount = 0;
        
        return true;
      } catch (error) {
        console.error('AlertDataReceiver连接或订阅失败:', error);
        
        // 检查连接重试次数
        if (this._connectRetryCount < this._maxConnectRetries) {
          this._connectRetryCount++;
          console.log(`告警连接失败，${this._connectRetryInterval/1000}秒后进行第${this._connectRetryCount}次重试...`);
          
          // 延迟后重试
          await new Promise(resolve => setTimeout(resolve, this._connectRetryInterval));
          this._connectingPromise = null; // 清除当前Promise
          return this.connect(); // 递归调用自身进行重试
        }
        
        throw error;
      } finally {
        this._connectingPromise = null; // 清除Promise引用
      }
    })();
    
    return this._connectingPromise;
  }

  /**
   * 带重试的订阅方法
   * @private
   */
  async _subscribeWithRetry() {
    // 如果已有订阅中的Promise，则返回该Promise
    if (this._subscribingPromise) {
      return this._subscribingPromise;
    }
    
    this._subscribingPromise = (async () => {
      try {
        // 检查连接状态
        if (!this._signalR.isConnected) {
          throw new Error('无法订阅：SignalR未连接');
        }
        
        console.log(`尝试订阅告警数据主题: ${this._topic}`);
        await this._signalR.subscribe(this._topic);
        this._isSubscribed = true;
        console.log('告警数据订阅成功');
        
        // 重置重试计数器
        this._subscribeRetryCount = 0;
        
        return true;
      } catch (error) {
        console.error('告警数据订阅失败:', error);
        
        // 检查订阅重试次数
        if (this._subscribeRetryCount < this._maxSubscribeRetries) {
          this._subscribeRetryCount++;
          console.log(`订阅失败，${this._subscribeRetryInterval/1000}秒后进行第${this._subscribeRetryCount}次重试...`);
          
          // 延迟后重试
          await new Promise(resolve => setTimeout(resolve, this._subscribeRetryInterval));
          this._subscribingPromise = null; // 清除当前Promise
          return this._subscribeWithRetry(); // 递归调用自身进行重试
        }
        
        throw error;
      } finally {
        this._subscribingPromise = null; // 清除Promise引用
      }
    })();
    
    return this._subscribingPromise;
  }

  /**
   * 处理连接状态变化
   * @param {boolean} isConnected - 是否已连接
   * @param {string} connectionId - 连接ID
   * @param {string} detail - 详细信息
   * @private
   */
  _handleConnectionStatusChanged(isConnected, connectionId, detail) {
    console.log('AlertDataReceiver连接状态变更:', isConnected, connectionId, detail || '已连接');
    
    // 更新连接状态
    const previousState = this._isConnected;
    this._isConnected = isConnected;
    
    // 如果连接断开后又重新连接，需要重新订阅
    if (isConnected && !this._isSubscribed) {
      // 使用延迟重新订阅，避免与其他组件竞争
      setTimeout(() => {
        if (this._isConnected && !this._isSubscribed) {
          console.log('告警连接已恢复，尝试重新订阅...');
          this._subscribeWithRetry().then(() => {
            console.log('AlertDataReceiver重新订阅成功');
          }).catch(err => {
            console.error('AlertDataReceiver重新订阅失败:', err);
          });
        }
      }, 1000);
    } else if (!isConnected && previousState) {
      // 连接断开，更新订阅状态
      this._isSubscribed = false;
      console.log('告警连接断开，已标记为未订阅');
    }
    
    // 触发连接状态变更事件
    eventBus.$emit('alert-connection-status-changed', {
      isConnected,
      connectionId,
      detail
    });
  }

  _getUserIds(alertData) {
    if(!alertData) {
      return null;
    }
    if(alertData.userIds) {
      return alertData.userIds;
    }
    if(!alertData.metaData || !alertData.metaData.userIds) {
      return null;
    }
    
    return alertData.metaData.userIds;
  }

  /**
   * 处理接收到的告警消息
   * @param {Object} message - 接收到的消息
   * @param {Object} parsedData - 解析后的数据
   * @private
   */
  _handleAlertMessage(message, parsedData) {
    try {
      const alertData = parsedData;
      
      if (!alertData || !alertData.alertId) {
        console.warn('AlertDataReceiver: 无效的告警数据:', alertData);
        return;
      }

      // 仅处理alertData.metaData.userIds中包含当前用户ID的告警
      // 当前用户为admin权限时，处理所有告警
      // 当前用户ID从localStorage中获取
      const userIds = this._getUserIds(alertData);
      //获取当前用户ID并转换为数字
      const userId = Number(localStorage.getItem('userId'));
      const isAdmin = localStorage.getItem('isAdmin') === 'true';
      // console.warn('【调试】userIds:', userIds, 'userId:', userId, 'isAdmin:', isAdmin);
      if(!userIds || userIds.length === 0) {
        console.warn('AlertDataReceiver: 告警缺少 userIds，跳过播报, alertId=', alertData.alertId);
        return;
      }
      // 仅桥梁用户或管理员才播报告警
      if (!userIds.includes(userId) && !isAdmin && userId !== 'admin') {
        console.warn('AlertDataReceiver: 当前用户不在告警接收人范围内，跳过播报, userId=', userId, 'userIds=', userIds);
        return;
      }
      
      // 转换为标准告警对象格式
      const alert = this._convertToAlertObject(alertData,message.metaData);
      
      // console.warn('【调试】alert:', alert);
      // 如果告警类型为穿越、进入、离开、超高，则使用TTS播报告警消息
      if(alert.type === 'CROSSING' || alert.type === 'ENTRY' || alert.type === 'EXIT'
        || alert.type === 'HEIGHT' || alert.type === 'HIGH') {
        // TODO：仅当alert.noticeType为VHF时，才使用TTS播报告警消息
        // if(alert.noticeType !== 'VHF') {
        //   return;
        // }

        // 输出TTS播报信息，包括mmsi、noticeType、 alert.type、alert.description
        console.log('【调试】TTS播报信息：mmsi = ', alert.mmsi, 'noticeType = ', alert.noticeType, 'alert.type = ', alert.type, 'alert.description = ', alert.description);
        // 使用TTS播报告警消息
        if (this._ttsEnabled && alert.description) {
          this._speakAlert(alert.description);
        }
        return;
    }

      // 检查是否已存在相同ID的告警，避免重复添加
      const existingAlertIndex = this._alerts.findIndex(a => a.id === alert.id);
      
      // 如果告警已存在，则不再添加
      if (existingAlertIndex >= 0) {
        console.log(`AlertDataReceiver: 跳过重复的告警 ID: ${alert.id}`);
        return;
      }
      
      // 添加到告警数组的开头（最新的告警显示在前面）
      this._alerts.unshift(alert);
      
      // 限制历史记录数量
      if (this._alerts.length > this._maxAlertHistory) {
        this._alerts = this._alerts.slice(0, this._maxAlertHistory);
      }
      
      // 触发告警更新事件
      eventBus.$emit('alert-data-updated', {
        alert,
        alerts: this._alerts
      });
      
      // 使用 eventBus 广播告警，用于走马灯显示
      // console.warn('【告警广播】通过 eventBus 广播告警消息:', alert.id, alert.description);
      eventBus.$emit('alert-received', alert);



      
      // 使用TTS播报告警消息
      if (this._ttsEnabled && alert.description) {
        this._speakAlert(alert.description);
      }
      
    } catch (error) {
      console.error('AlertDataReceiver处理告警数据失败:', error);
    }
  }

  /**
   * 提取_handleAlertMessage为单独的函数，可用于外部直接调用，用于处理手动发送的告警（不需要parse数据）
   * 调用示例：alertDataReceiver.handleManualAlert(alertData)
   * @param {Object} alertData - 告警数据
   * @returns {Object} 告警对象
   */
  handleManualAlert(alertData) {
    // const alert = this._convertToAlertObject(alertData);
    // console.warn('【接收到手动告警】：',alertData)
    // this._handleAlertMessage(alert, alert);
    
    console.warn('【接收到手动告警】：',alertData)
    
    // 检查手动告警是否应该显示
    const shouldDisplay = alertFilter.shouldDisplay(alertData.description);
    if (!shouldDisplay) {
      console.log(`AlertDataReceiver: 手动告警 "${alertData.description}" 不包含关键字，已过滤`);
      return;
    }
    
    const userId = localStorage.getItem('userId');
    alertData.metaData = {
      userIds: [userId],
      noticeType: "VHF,PAGE,RECORD"
    }
    this._handleAlertMessage(alertData, alertData);
  }

  /**
   * 将原始数据转换为标准告警对象
   * @param {Object} rawData - 原始告警数据
   * @returns {Object} 标准告警对象
   * @private
   */
  _convertToAlertObject(rawData,metaData) {
    const alertTypeMap = {
      'AREA_ENTRY': '区域进入',
      'AREA_EXIT': '区域离开',
      'SPEED_OVER': '超速',
      'HEIGHT_OVER': '超高',
      'HEIGHT': '超高',
      'HIGH': '超高',
      'MEETING': '会遇',
      'COURSE_DEVIATION': '偏航',
      'STAY_OVERTIME': '滞留'
    };
    
    const alertLevelMap = {
      1: '警告',
      2: '警告',
      3: '警告'
    };

    // 需要先判断metaData是否存在，如果不存在，则使用rawData.noticeType
    const noticeType = metaData && metaData.noticeType?metaData.noticeType : rawData.noticeType;
    return {
      id: rawData.alertId,
      alertId:rawData.alertId,
      mmsi: rawData.vesselMmsi,
      type: rawData.alertType,
      typeText: alertTypeMap[rawData.alertType] || rawData.alertType,
      level: rawData.alertLevel,
      levelText: alertLevelMap[rawData.alertLevel] || `${rawData.alertLevel}级警告`,
      description: rawData.description,
      ruleType: rawData.alertType,
      sourceData: rawData.sourceData,
      longitude: rawData.longitude,
      latitude: rawData.latitude,
      time: this._formatTime(rawData.alertTime),
      originalTime: rawData.alertTime,
      noticeType: noticeType,
      position: {
        longitude: rawData.longitude,
        latitude: rawData.latitude
      },
      areaId: rawData.areaId
    };
  }

  /**
   * 格式化时间
   * @param {string} timeStr - ISO格式的时间字符串
   * @returns {string} 格式化后的时间字符串
   * @private
   */
  _formatTime(timeStr) {
    try {
      const date = new Date(timeStr);
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    } catch (e) {
      return timeStr || '未知时间';
    }
  }

  /**
   * 将数字转换为单个数字形式的字符串
   * 例如: "123" -> "1 2 3"，确保数字被单独读出
   * @param {string} text - 原始文本
   * @returns {string} 处理后的文本
   * @private
   */
  _formatNumbersForSpeech(text) {
    if (!text) return text;
    
    // 正则表达式匹配连续的数字
    return text.replace(/\d+/g, match => {
      // 将数字分开，用空格隔开每一位
      return match.split('').join(' ');
    });
  }

  /**
   * 启动播报历史记录定期清理
   * @private
   */
  _startSpeechHistoryCleanup() {
    // 定期清理过期的播报历史记录
    this._speechCleanupTimer = setInterval(() => {
      this._cleanupSpeechHistory();
    }, this._speechCleanupInterval);
  }

  /**
   * 清理过期的播报历史记录
   * @private
   */
  _cleanupSpeechHistory() {
    const now = Date.now();
    let cleanedCount = 0;
    
    // 遍历并清理超过时间窗口的记录
    for (const [text, timestamp] of this._speechHistory.entries()) {
      if (now - timestamp > this._speechDedupeWindow) {
        this._speechHistory.delete(text);
        cleanedCount++;
      }
    }
    
    if (cleanedCount > 0) {
      console.log(`AlertDataReceiver: 已清理 ${cleanedCount} 条过期的TTS播报历史记录`);
    }
  }

  /**
   * 检查是否为重复播报（5分钟内已播报过相同内容）
   * @param {string} text - 要检查的文本
   * @returns {boolean} 如果是重复播报则返回true
   * @private
   */
  _isDuplicateSpeech(text) {
    const now = Date.now();
    const lastSpeakTime = this._speechHistory.get(text);
    
    if (!lastSpeakTime) {
      return false; // 没有历史记录，不是重复
    }
    
    // 检查是否在时间窗口内
    const timeSinceLastSpeak = now - lastSpeakTime;
    if (timeSinceLastSpeak < this._speechDedupeWindow) {
      // 计算剩余时间（秒）
      const remainingSeconds = Math.ceil((this._speechDedupeWindow - timeSinceLastSpeak) / 1000);
      console.log(`AlertDataReceiver: 跳过重复播报（${remainingSeconds}秒内已播报）: "${text.substring(0, 30)}..."`);
      return true;
    }
    
    return false;
  }

  /**
   * 使用TTS播报告警消息
   * @param {string} text - 要播报的文本
   * @private
   */
  _speakAlert(text) {
    if (!this._speechSynthesis) {
      console.warn('当前浏览器不支持语音合成API');
      return;
    }
    
    // 检查是否为重复播报
    if (this._isDuplicateSpeech(text)) {
      return; // 跳过重复播报
    }
    
    // 记录本次播报的时间戳
    this._speechHistory.set(text, Date.now());
    
    // 处理文本中的数字，使其按单个数字读出
    const processedText = this._formatNumbersForSpeech(text);
    
    // 添加到语音队列（限制队列长度，避免长期堆积）
    if (this._speechQueue.length >= this._speechQueueMax) {
      this._speechQueue.shift();
      console.warn('AlertDataReceiver: 语音队列已满，丢弃最早的一条待播报内容');
    }
    this._speechQueue.push(processedText);
    
    // 如果当前没有在播报，开始播报
    if (!this._isSpeaking) {
      this._processNextSpeech();
    }
  }

  /**
   * 处理下一条语音播报
   * @private
   */
  _processNextSpeech() {
    if (this._speechQueue.length === 0) {
      this._isSpeaking = false;
      return;
    }
    
    this._isSpeaking = true;
    const text = this._speechQueue.shift();
    
    // 创建语音合成实例
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'zh-CN';
    utterance.rate = 1.0;
    utterance.pitch = 1.0;

    let finished = false;
    const finish = (reason) => {
      if (finished) return;
      finished = true;
      if (this._speechWatchdogTimer) {
        clearTimeout(this._speechWatchdogTimer);
        this._speechWatchdogTimer = null;
      }
      if (reason && reason !== 'end') {
        console.warn('AlertDataReceiver: 本次语音播报结束，原因=', reason);
      }
      setTimeout(() => {
        this._processNextSpeech();
      }, 300); // 添加短暂延迟，避免连续播报
    };
    
    // 播报结束后处理下一条
    utterance.onend = () => finish('end');
    
    // 出错时也继续处理队列
    utterance.onerror = (event) => {
      const err = (event && event.error) || 'unknown';
      console.error('语音合成出错:', err, event);
      if ((err === 'not-allowed' || err === 'audio-busy') && !this._speechBlockedWarned) {
        this._speechBlockedWarned = true;
        console.warn('浏览器阻止了语音播报：请先点击页面任意位置，并检查标签页是否被静音、系统是否有可用音频输出设备');
      }
      finish('error');
    };

    // 看门狗：部分浏览器（尤其后台标签页）不会触发 onend，避免队列被永久卡死
    const expectedMs = Math.max(10000, text.length * 500);
    this._speechWatchdogTimer = setTimeout(() => {
      console.warn('AlertDataReceiver: 语音播报超时未结束，强制继续后续播报');
      try {
        this._speechSynthesis.cancel();
      } catch (e) {
        // ignore
      }
      finish('timeout');
    }, expectedMs);
    
    // 开始播报
    try {
      if (this._speechSynthesis.paused) {
        this._speechSynthesis.resume();
      }
      this._speechSynthesis.speak(utterance);
    } catch (e) {
      console.error('调用语音合成失败:', e);
      finish('exception');
    }
  }

  /**
   * 启用或禁用TTS播报
   * @param {boolean} enabled - 是否启用TTS播报
   */
  setTTSEnabled(enabled) {
    this._ttsEnabled = enabled;
    console.log(`AlertDataReceiver: TTS播报${enabled ? '已启用' : '已禁用'}`);
    
    // 如果禁用TTS，取消所有待播报的语音
    if (!enabled && this._speechSynthesis) {
      this._speechSynthesis.cancel();
      this._speechQueue = [];
      this._isSpeaking = false;
      // 清空播报历史
      this._speechHistory.clear();
    }
  }

  /**
   * 获取当前TTS播报状态
   * @returns {boolean} 是否启用TTS播报
   */
  get isTTSEnabled() {
    return this._ttsEnabled;
  }

  /**
   * 断开连接并清理资源
   * @returns {Promise<void>}
   */
  async disconnect() {
    // 停止播报历史清理定时器
    if (this._speechCleanupTimer) {
      clearInterval(this._speechCleanupTimer);
      this._speechCleanupTimer = null;
    }
    
    // 取消所有待播报的语音
    if (this._speechSynthesis) {
      this._speechSynthesis.cancel();
      this._speechQueue = [];
      this._isSpeaking = false;
    }
    if (this._speechWatchdogTimer) {
      clearTimeout(this._speechWatchdogTimer);
      this._speechWatchdogTimer = null;
    }
    
    // 清空播报历史
    this._speechHistory.clear();
    
    if (this._isSubscribed && this._signalR) {
      try {
        await this._signalR.unsubscribe(this._topic);
        this._isSubscribed = false;
        console.log('AlertDataReceiver已取消订阅');
      } catch (err) {
        console.warn('AlertDataReceiver取消订阅失败:', err);
      }
    }
  }

  /**
   * 清空告警历史
   */
  clearAlerts() {
    this._alerts = [];
    eventBus.$emit('alert-data-updated', {
      alerts: this._alerts
    });
    console.log('AlertDataReceiver: 已清空告警历史');
  }
  
  /**
   * 刷新告警列表
   * 当告警被外部处理后调用此方法触发更新
   */
  refreshAlerts() {
    // 触发告警数据更新事件，通知订阅者刷新数据
    eventBus.$emit('alert-data-updated', {
      alerts: this._alerts
    });
    console.log('AlertDataReceiver: 已刷新告警列表');
  }

  /**
   * 获取当前告警数据列表
   * @returns {Array} 告警数据数组
   */
  get alerts() {
    return [...this._alerts]; // 返回副本，防止外部修改
  }

  /**
   * 获取连接状态
   * @returns {boolean} 是否已连接
   */
  get isConnected() {
    return this._isConnected;
  }

  /**
   * 获取订阅状态
   * @returns {boolean} 是否已订阅
   */
  get isSubscribed() {
    return this._isSubscribed;
  }

  /**
   * 获取告警数量
   * @returns {number} 告警数量
   */
  get alertCount() {
    return this._alerts.length;
  }

  /**
   * 根据类型筛选告警
   * @param {Array} types - 告警类型数组
   * @returns {Array} 筛选后的告警数组
   */
  filterByTypes(types) {
    if (!types || types.length === 0) {
      return this._alerts;
    }
    return this._alerts.filter(alert => types.includes(alert.type));
  }

  /**
   * 获取告警过滤器实例
   * @returns {Object} 告警过滤器实例
   */
  getAlertFilter() {
    return alertFilter;
  }

  /**
   * 设置告警过滤器的启用状态
   * @param {boolean} enabled - 是否启用过滤
   */
  setAlertFilterEnabled(enabled) {
    alertFilter.setEnabled(enabled);
    console.log(`AlertDataReceiver: 告警过滤功能已${enabled ? '启用' : '禁用'}`);
  }

  /**
   * 添加告警过滤关键字
   * @param {string} keyword - 关键字
   * @param {Array<string>} variants - 变体（可选）
   */
  addAlertFilterKeyword(keyword, variants = []) {
    alertFilter.addKeyword(keyword, variants);
  }

  /**
   * 移除告警过滤关键字
   * @param {string} keyword - 关键字
   */
  removeAlertFilterKeyword(keyword) {
    alertFilter.removeKeyword(keyword);
  }

  /**
   * 测试告警消息是否通过过滤
   * @param {string} message - 告警消息
   * @returns {Object} 测试结果
   */
  testAlertMessage(message) {
    return alertFilter.testMessage(message);
  }
}

// 事件监听器助手方法
const alertEvents = {
  /**
   * 监听告警数据更新事件
   * @param {Function} callback - 回调函数
   */
  onAlertDataUpdated(callback) {
    eventBus.$on('alert-data-updated', callback);
  },

  /**
   * 移除告警数据更新事件监听
   * @param {Function} callback - 回调函数
   */
  offAlertDataUpdated(callback) {
    eventBus.$off('alert-data-updated', callback);
  },

  /**
   * 监听连接状态变更事件
   * @param {Function} callback - 回调函数
   */
  onConnectionStatusChanged(callback) {
    eventBus.$on('alert-connection-status-changed', callback);
  },

  /**
   * 移除连接状态变更事件监听
   * @param {Function} callback - 回调函数
   */
  offConnectionStatusChanged(callback) {
    eventBus.$off('alert-connection-status-changed', callback);
  }
};

// 创建并导出单例实例
const alertDataReceiver = new AlertDataReceiver();

export { alertDataReceiver, alertEvents };
export default alertDataReceiver; 